import type { LocalBusiness, WithContext } from 'schema-dts'
import type { EventDescription, Supplier } from '~/composables/types/event'

const usePartnerSchema = (
  supplier: Supplier,
  events: EventDescription[]
): any => {
  const schema = ref<WithContext<LocalBusiness> | undefined>(undefined)
  const script = computed(() => ({
    type: 'application/ld+json',
    innerHTML: schema.value,
  }))

  const {
    public: { baseUrl },
  } = useRuntimeConfig()
  const fullLocalizedUrl = useFullLocalizedUrl()
  const createSchema = (): WithContext<LocalBusiness> => {
    const reviewSchemaArr = supplierGetters
      .getReviewsList(supplier)
      .map((review) => {
        return {
          '@type': 'Review',
          reviewRating: {
            '@type': 'Rating',
            ratingValue: reviewGetters.getReviewRating(review),
          },
          reviewBody: reviewGetters.getReview(review),
          author: {
            '@type': 'Person',
            name: reviewGetters.getAuthor(review),
          },
        }
      })

    return {
      '@context': 'https://schema.org',
      '@type': supplierGetters.getSeoBusinessType(supplier) || 'LocalBusiness',
      image: [
        supplierGetters.getLogo(supplier),
        ...events
          .map((event) =>
            eventGetters.getGalleryImages(event).map((x) => x.thumb)
          )
          .reduce((acc, val) => acc.concat(val), []),
      ],
      name: supplierGetters.getName(supplier),
      address: supplierGetters.getAddress(supplier),
      brand: {
        '@type': 'Brand',
        name: supplierGetters.getName(supplier),
        url: fullLocalizedUrl(`/p/${supplierGetters.getPermalink(supplier)}`),
      },
      url: `${baseUrl}/de-de/p/${supplierGetters.getPermalink(supplier)}/`,
      ...(supplierGetters.getReviewsTotal(supplier) &&
        supplierGetters.getReviewsTotal(supplier) > 0 && {
          review: reviewSchemaArr,
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: supplierGetters.getReviewsAverage(supplier),
            bestRating: 5,
            worstRating: 1,
            ratingCount: supplierGetters.getReviewsTotal(supplier),
          },
        }),
    }
  }

  schema.value = createSchema()

  return {
    schema,
    script,
  }
}

export default usePartnerSchema
